import type { Simulator } from "../providers/vehiclesProvider";
import { Create, EditBase, Resource, Tab, useRedirect } from "react-admin";
import { SimulatorForm } from "../details/vehicles/SimulatorForm";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Tabs } from "../misc/Tabs";
import { VehicleCalendar } from "../details/vehicles/VehicleCalendar";
import { grants } from "../backoffice.access_control";
import { VehicleDetailsHeader } from "../details/vehicles/VehicleDetailsHeader";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { AccessDenied } from "../misc/AccessDenied";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { DefaultList } from "../misc/DefaultList";
import { VehicleDataCard } from "../details/vehicles/VehicleDataCard";

function CreateSimulatorForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  useEffect(() => {
    if (!grants.has("Vehicle:create") && drivingSchoolId) {
      redirect(`/drivingSchools/${drivingSchoolId}/fleet#simulators`);
    }
  }, []);

  if (!grants.has("Vehicle:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create<Simulator>
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: ({ name }) => {
          // eslint-disable-next-line react-compiler/react-compiler
          window.onNavigation = () => {
            delete window.onNavigation;
            showSuccessToast(`Simulator ${name} erfolgreich gespeichert`);
          };
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#simulators`);
        },
      }}
    >
      <SimulatorForm mode="create" sx={{ padding: overviewCardStyle.padding }} />
    </Create>
  );
}

function SimulatorDetails() {
  const redirect = useRedirect();
  const [{ drivingSchoolId }] = useAutovioContext();
  const simulator = useRecordAndSwitchDrivingSchoolIfNecessary<Simulator>((it) => it.drivingSchoolId);

  if (!simulator) {
    return <LoadingIndicator />;
  }

  return (
    <EditBase
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert");
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#simulators`);
        },
      }}
    >
      <Tabs
        sx={{ ...overviewCardStyle, padding: { xs: "5px 16px 16px", sm: "10px 30px" } }}
        header={<VehicleDetailsHeader />}
        mobileSelectAriaLabel="Fahrzeugbereich"
        syncWithLocation={false}
        syncWithHash
        separateCards
      >
        <Tab label="Kalender" value="calendar">
          <VehicleCalendar />
        </Tab>
        {grants.has("Vehicle:edit") && (
          <Tab label="Simulator-Daten" value="data">
            <VehicleDataCard>
              <SimulatorForm mode="edit" />
            </VehicleDataCard>
          </Tab>
        )}
      </Tabs>
    </EditBase>
  );
}

export const simulatorsResource = (
  <Resource
    key="simulators"
    name="simulators"
    list={DefaultList}
    create={CreateSimulatorForm}
    edit={SimulatorDetails}
    options={{
      label: "Simulatoren",
      createTitle: "Neuer Simulator",
      noEntriesText: "Noch kein Simulator hinterlegt.",
      noMatchingEntriesText: "Keine passenden Simulatoren gefunden.",
    }}
    recordRepresentation={(record: Simulator) => record.name}
  />
);
